import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useDashboard } from "../context/DashboardContext";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/layout/PageHeader";
import { formatCompactINR, formatINR, formatNum } from "../lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Wallet, Save, Target, TrendingUp } from "lucide-react";

function monthKeys(n = 6) {
  const out = [];
  const d = new Date();
  for (let i = 0; i < n; i++) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}
function weekKeys(n = 8) {
  const out = [];
  const d = new Date();
  for (let i = 0; i < n; i++) {
    const t = new Date(d);
    const day = (t.getDay() + 6) % 7;
    t.setDate(t.getDate() - day + 3);
    const week1 = new Date(t.getFullYear(), 0, 4);
    const wk = 1 + Math.round(((t - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
    out.push(`${t.getFullYear()}-W${String(wk).padStart(2, "0")}`);
    d.setDate(d.getDate() - 7);
  }
  return out;
}

export default function Revenue() {
  const { branchId, branches } = useDashboard();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const editable = isAdmin && branchId !== "all";

  const [periodType, setPeriodType] = useState("monthly");
  const [periodKey, setPeriodKey] = useState(monthKeys()[0]);
  const [services, setServices] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [amounts, setAmounts] = useState({});
  const [targets, setTargets] = useState({});
  const [plan, setPlan] = useState(null);

  const periodOptions = periodType === "monthly" ? monthKeys(6) : weekKeys(8);

  const loadServices = useCallback(async () => {
    const { data } = await api.get("/services");
    setServices(data);
  }, []);

  const loadComparison = useCallback(async () => {
    const { data } = await api.get("/revenue/comparison", { params: { branch_id: branchId, period_type: periodType } });
    setComparison(data);
  }, [branchId, periodType]);

  const loadEntries = useCallback(async () => {
    const { data } = await api.get("/revenue", { params: { branch_id: branchId, period_type: periodType } });
    const m = {};
    data.filter((e) => e.period_key === periodKey).forEach((e) => { m[e.service] = e.amount; });
    setAmounts(m);
  }, [branchId, periodType, periodKey]);

  const loadPlan = useCallback(async () => {
    const { data } = await api.get("/planning", { params: { branch_id: branchId, period_key: periodKey } });
    setPlan(data);
    const t = {};
    data.services.forEach((s) => { t[s.name] = s.target_revenue; });
    setTargets(t);
  }, [branchId, periodKey]);

  useEffect(() => { loadServices(); }, [loadServices]);
  useEffect(() => { loadComparison(); }, [loadComparison]);
  useEffect(() => { loadEntries(); }, [loadEntries]);
  useEffect(() => { loadPlan(); }, [loadPlan]);
  useEffect(() => { setPeriodKey(periodType === "monthly" ? monthKeys()[0] : weekKeys()[0]); }, [periodType]);

  const saveEarning = async (service) => {
    try {
      await api.post("/revenue", {
        branch_id: branchId, service, period_type: periodType,
        period_key: periodKey, amount: Number(amounts[service] || 0),
      });
      toast.success(`Saved ${service}`);
      loadComparison();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const saveTarget = async (service) => {
    try {
      await api.put("/planning", {
        branch_id: branchId, service, period_key: periodKey,
        target_revenue: Number(targets[service] || 0),
      });
      toast.success(`Target set for ${service}`);
      loadPlan();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const summary = comparison?.service_summary || [];
  const totalEarn = summary.reduce((s, x) => s + x.earnings, 0);
  const totalSpend = summary.reduce((s, x) => s + x.spend, 0);
  const inp = "bg-[#0F172A] border-white/10 text-white h-9 focus-visible:ring-[#38BDF8]";

  const branchName = branchId === "all" ? "All Branches" : branches.find((b) => b.id === branchId)?.name;

  return (
    <div className="space-y-6 animate-fade-up">
      <PageHeader title="Revenue & Lead Planning"
        subtitle={`Actual earnings vs ad spend, and reverse-engineered lead targets · ${branchName}`}
        action={
          <Select value={periodType} onValueChange={setPeriodType}>
            <SelectTrigger data-testid="period-type" className="h-9 w-[130px] bg-[#1E293B] border-white/10 text-slate-200 rounded-full text-xs"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#1E293B] border-white/10 text-slate-200">
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
            </SelectContent>
          </Select>
        } />

      {/* Summary strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-xl bg-[#1E293B] border border-white/10 p-5">
          <p className="text-xs uppercase tracking-wider text-slate-400">Total Earnings</p>
          <p className="font-display text-2xl font-bold mt-2 text-[#10B981]">{formatCompactINR(totalEarn)}</p>
        </div>
        <div className="rounded-xl bg-[#1E293B] border border-white/10 p-5">
          <p className="text-xs uppercase tracking-wider text-slate-400">Ad Spend</p>
          <p className="font-display text-2xl font-bold mt-2">{formatCompactINR(totalSpend)}</p>
        </div>
        <div className="rounded-xl bg-[#1E293B] border border-white/10 p-5">
          <p className="text-xs uppercase tracking-wider text-slate-400">Actual ROAS</p>
          <p className="font-display text-2xl font-bold mt-2 text-[#38BDF8]">{totalSpend ? (totalEarn / totalSpend).toFixed(2) : "0.00"}x</p>
        </div>
      </div>

      <Tabs defaultValue="earnings">
        <TabsList className="bg-[#1E293B] border border-white/10">
          <TabsTrigger value="earnings" data-testid="rev-tab-earnings" className="data-[state=active]:bg-[#38BDF8] data-[state=active]:text-[#0F172A]">Earnings</TabsTrigger>
          <TabsTrigger value="trend" data-testid="rev-tab-trend" className="data-[state=active]:bg-[#38BDF8] data-[state=active]:text-[#0F172A]">Revenue Trend</TabsTrigger>
          <TabsTrigger value="planning" data-testid="rev-tab-planning" className="data-[state=active]:bg-[#38BDF8] data-[state=active]:text-[#0F172A]">Lead Planning</TabsTrigger>
        </TabsList>

        {/* Earnings entry */}
        <TabsContent value="earnings" className="mt-5 space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-400">Period</span>
            <Select value={periodKey} onValueChange={setPeriodKey}>
              <SelectTrigger data-testid="period-key" className="h-9 w-[150px] bg-[#1E293B] border-white/10 text-slate-200 rounded-full text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1E293B] border-white/10 text-slate-200">
                {periodOptions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            {!editable && <span className="text-xs text-[#F59E0B]">Select a specific branch as admin to edit earnings</span>}
          </div>
          <div className="rounded-xl bg-[#1E293B] border border-white/10 divide-y divide-white/5">
            {services.map((s) => {
              const sm = summary.find((x) => x.name === s.name) || {};
              return (
                <div key={s.id} className="flex items-center gap-3 p-4">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: s.color }} />
                  <span className="text-sm text-slate-200 flex-1 min-w-0 truncate">{s.name}</span>
                  <span className="hidden sm:block text-xs text-slate-500 w-28 text-right">Spend {formatCompactINR(sm.spend || 0)}</span>
                  <span className="hidden sm:block text-xs w-24 text-right" style={{ color: (sm.actual_roas || 0) >= 1 ? "#10B981" : "#EF4444" }}>{sm.actual_roas || 0}x ROAS</span>
                  <div className="flex items-center gap-2">
                    <Input data-testid={`earning-${s.name}`} type="number" placeholder="₹ earnings"
                      value={amounts[s.name] ?? ""} disabled={!editable}
                      onChange={(e) => setAmounts({ ...amounts, [s.name]: e.target.value })}
                      className={`${inp} w-36`} />
                    {editable && (
                      <Button data-testid={`save-earning-${s.name}`} onClick={() => saveEarning(s.name)}
                        className="h-9 w-9 p-0 bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0F172A] rounded-lg"><Save className="w-4 h-4" /></Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* Trend */}
        <TabsContent value="trend" className="mt-5">
          <div className="rounded-xl bg-[#1E293B] border border-white/10 p-6">
            <div className="flex items-center gap-2 mb-5">
              <TrendingUp className="w-5 h-5 text-[#38BDF8]" />
              <h2 className="font-display text-lg font-semibold">Earnings by Service over {periodType === "monthly" ? "Months" : "Weeks"}</h2>
            </div>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={comparison?.chart || []} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                  <CartesianGrid stroke="#334155" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="period" tick={{ fill: "#64748B", fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#64748B", fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1e5).toFixed(0)}L`} />
                  <Tooltip contentStyle={{ background: "#1E293B", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
                    formatter={(v) => formatCompactINR(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
                  {services.map((s) => (
                    <Bar key={s.id} dataKey={s.name} stackId="a" fill={s.color} radius={[0, 0, 0, 0]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
            {(!comparison?.chart || comparison.chart.length === 0) && (
              <p className="text-center text-slate-500 text-sm mt-4">No earnings entered yet. Add earnings under the Earnings tab.</p>
            )}
          </div>
        </TabsContent>

        {/* Planning */}
        <TabsContent value="planning" className="mt-5 space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-400">Target period</span>
            <Select value={periodKey} onValueChange={setPeriodKey}>
              <SelectTrigger data-testid="plan-period-key" className="h-9 w-[150px] bg-[#1E293B] border-white/10 text-slate-200 rounded-full text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1E293B] border-white/10 text-slate-200">
                {periodOptions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-xs text-slate-500">Set a revenue goal → we compute the leads & budget needed at current CPL.</span>
          </div>
          <div className="rounded-xl bg-[#1E293B] border border-white/10 overflow-x-auto">
            <table className="w-full text-sm min-w-[820px]">
              <thead>
                <tr className="text-slate-400 text-xs uppercase tracking-wider border-b border-white/10">
                  <th className="text-left font-medium py-3 px-4">Service</th>
                  <th className="text-right font-medium py-3 px-4">Target Revenue</th>
                  <th className="text-right font-medium py-3 px-4">Ticket / Close</th>
                  <th className="text-right font-medium py-3 px-4">Current CPL</th>
                  <th className="text-right font-medium py-3 px-4">Bookings</th>
                  <th className="text-right font-medium py-3 px-4">Leads Needed</th>
                  <th className="text-right font-medium py-3 px-4">Est. Budget</th>
                  {editable && <th className="py-3 px-4"></th>}
                </tr>
              </thead>
              <tbody>
                {(plan?.services || []).map((s) => (
                  <tr key={s.name} className="border-b border-white/5">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
                        <span className="text-slate-200">{s.name}</span></div>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <Input data-testid={`target-${s.name}`} type="number" placeholder="₹ goal"
                        value={targets[s.name] ?? ""} disabled={!editable}
                        onChange={(e) => setTargets({ ...targets, [s.name]: e.target.value })}
                        className={`${inp} w-32 ml-auto text-right`} />
                    </td>
                    <td className="py-3 px-4 text-right text-slate-400 text-xs tabular">{formatCompactINR(s.avg_ticket_size)} · {(s.close_rate * 100).toFixed(0)}%</td>
                    <td className="py-3 px-4 text-right tabular text-slate-300">{formatINR(s.current_cpl)}</td>
                    <td className="py-3 px-4 text-right tabular text-slate-300">{formatNum(s.required_bookings)}</td>
                    <td className="py-3 px-4 text-right tabular font-semibold text-[#38BDF8]">{formatNum(s.required_leads)}</td>
                    <td className="py-3 px-4 text-right tabular font-semibold text-[#10B981]">{formatCompactINR(s.est_budget)}</td>
                    {editable && (
                      <td className="py-3 px-4 text-right">
                        <Button data-testid={`save-target-${s.name}`} onClick={() => saveTarget(s.name)}
                          className="h-8 px-3 bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0F172A] rounded-lg text-xs"><Target className="w-3.5 h-3.5 mr-1" />Set</Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!editable && <p className="text-xs text-[#F59E0B]">Select a specific branch as admin to set targets.</p>}
        </TabsContent>
      </Tabs>
    </div>
  );
}
